// Pure helpers for "Peru osinkokäsittely" (#1434). The server contract is #1433 v2:
// GET|DELETE /transactions/transactions/delete_by_dividend_instance/<id>/[?portfolio=<id>]
// returns {dividend_id, count, portfolio_count, skip_marker_count, portfolios[], skip_markers[]};
// DELETE adds deleted, skip_markers_deleted and portfolios[].deleted. Fields that are
// missing (older or additive server versions) fall back to safe defaults.

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const sameId = (a, b) =>
  a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

export const normalizeDividendUndoPreview = (data, currentPortfolioId) => {
  const rawPortfolios = Array.isArray(data?.portfolios) ? data.portfolios : [];

  const portfolios = rawPortfolios.map((p) => {
    const currency = p?.currency_label || "";
    const transactions = Array.isArray(p?.transactions) ? p.transactions : [];
    return {
      id: p?.portfolio_id,
      name: p?.portfolio_name || "",
      currency,
      count: toNumber(p?.count ?? transactions.length),
      total: toNumber(p?.price_total_sum),
      hiddenByCutoff: Boolean(p?.hidden_by_cutoff),
      skipMarker: Boolean(p?.skip_marker),
      isCurrent: sameId(p?.portfolio_id, currentPortfolioId),
      rows: transactions.map((trx) => ({
        id: trx?.id,
        date: trx?.date_transaction || null,
        amount: toNumber(trx?.price_total),
        currency: trx?.currency_label || currency,
      })),
    };
  });

  // The current portfolio first; the server's order (by name) otherwise.
  const ordered = [
    ...portfolios.filter((p) => p.isCurrent),
    ...portfolios.filter((p) => !p.isCurrent),
  ];

  const skipMarkers = (Array.isArray(data?.skip_markers) ? data.skip_markers : []).map((m) => ({
    id: m?.portfolio_id,
    name: m?.portfolio_name || "",
    isCurrent: sameId(m?.portfolio_id, currentPortfolioId),
  }));

  return {
    count: toNumber(data?.count),
    portfolioCount: toNumber(data?.portfolio_count ?? portfolios.length),
    skipMarkerCount: toNumber(data?.skip_marker_count ?? skipMarkers.length),
    skipMarkers,
    currentInPreview: ordered.some((p) => p.isCurrent),
    portfolios: ordered,
  };
};

// The DELETE response is already limited by the server (the portfolio parameter or the
// portfolios the user can see), so every portfolio in it is one the user may be told about.
export const summarizeDividendUndoResult = (res) => {
  const data = res?.data ?? res ?? {};
  const portfolios = Array.isArray(data?.portfolios) ? data.portfolios : [];
  const touched = portfolios.filter((p) => toNumber(p?.deleted) > 0 || Boolean(p?.skip_marker));
  const hidden = touched.filter((p) => Boolean(p?.hidden_by_cutoff));
  return {
    count: toNumber(data?.deleted),
    skipMarkersDeleted: toNumber(data?.skip_markers_deleted),
    portfolioNames: touched.map((p) => p?.portfolio_name || ""),
    hiddenByCutoff: hidden.length > 0,
    hiddenPortfolioNames: hidden.map((p) => p?.portfolio_name || ""),
  };
};

export const buildDividendUndoDoneMessage = (summary, t) => {
  const portfolios = (summary?.portfolioNames || []).join(", ");
  const parts = [
    portfolios
      ? t("dividend_undo_done_message", "Poistettu {{count}} kirjausta salkuista: {{portfolios}}.", {
          count: summary?.count ?? 0,
          portfolios,
        })
      : t("dividend_undo_done_message_no_portfolios", "Poistettu {{count}} kirjausta.", {
          count: summary?.count ?? 0,
        }),
  ];
  if (summary?.skipMarkersDeleted > 0) {
    parts.push(
      t("dividend_undo_done_skip_markers", "Ohitusmerkintöjä poistettu: {{count}}.", {
        count: summary.skipMarkersDeleted,
      })
    );
  }
  if (summary?.hiddenByCutoff) {
    parts.push(
      t(
        "dividend_undo_hidden_by_cutoff",
        "Osinko ei näy oletuksena Käsittele osingot -listalla salkuissa {{portfolios}}, koska niissä on myöhempi käsitelty osinko. Näytä myös vanhat -kytkin on käännetty päälle.",
        { portfolios: (summary.hiddenPortfolioNames || []).join(", ") }
      )
    );
  }
  return parts.join(" ");
};

// After a successful undo: turn "Näytä myös vanhat" on first, so that the reload done by
// handleResolve() already fetches the dividends behind the cutoff (Q468 a).
export const applyDividendUndoDone = ({ summary, t, notify, setShowOlder, handleResolve }) => {
  if (summary?.hiddenByCutoff && setShowOlder) {
    setShowOlder(true);
  }
  if (notify) {
    notify({
      title: t("dividend_undo_done_title", "Kirjaus"),
      message: buildDividendUndoDoneMessage(summary, t),
      autoDismiss: summary?.hiddenByCutoff ? 15 : 7,
    });
  }
  if (handleResolve) {
    handleResolve();
  }
};
